import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { addMonths } from 'date-fns';
import { useState } from 'react';
import { useParams } from 'react-router';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';
import { Matches } from '@sorare/gameplay/src/components/seo/Matches';

import type {
  ClubFixturesQuery,
  ClubFixturesQueryVariables,
} from './__generated__/page.graphql.ts';

const CLUB_FIXTURES_QUERY = gql`
  query ClubFixturesQuery(
    $slug: String!
    $cursor: String
    $futureCursor: String
    $startDate: ISO8601DateTime!
    $endDate: ISO8601DateTime!
  ) {
    football {
      club(slug: $slug) {
        slug
        name
        futureGames: games(
          startDate: $startDate
          endDate: $endDate
          first: 20
          after: $futureCursor
        ) {
          nodes {
            id
            ...Matches_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        pastGames: latestGames(first: 20, after: $cursor) {
          nodes {
            id
            ...Matches_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${Matches.fragments.anyGameInterface}
` as TypedDocumentNode<ClubFixturesQuery, ClubFixturesQueryVariables>;

const params = {
  startDate: new Date(),
  endDate: addMonths(new Date(), 2),
};

const Fixtures = () => {
  const { slug } = useParams();
  const openDialog = useOpenDialog();
  const [tab, setTab] = useState<'past' | 'future'>('future');
  const { data, loading, loadMore } = usePaginatedQuery(CLUB_FIXTURES_QUERY, {
    variables: {
      slug: slug || '',
      ...params,
    },
    connection: 'GameConnection',
    skip: !slug,
  });
  const displayName = data?.football.club?.name || '';
  useTitleAndDescription(
    metadatas.clubFixtures.title,
    metadatas.clubFixtures.description,
    {
      club: displayName,
    }
  );
  const { futureGames: futureGamesRoot, pastGames } = data?.football.club || {};

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        futureCursor: futureGamesRoot?.pageInfo.endCursor,
        cursor: pastGames?.pageInfo.endCursor,
      });
    },
    (tab === 'future' && !!futureGamesRoot?.pageInfo.hasNextPage) ||
      (tab === 'past' && !!pastGames?.pageInfo.hasNextPage),
    loading
  );

  const futureGames = futureGamesRoot?.nodes || [];
  if (tab === 'future' && futureGames.length <= 1 && !loading) {
    setTab('past');
  }

  return (
    <Matches
      displayName={displayName}
      loading={loading}
      pastGames={pastGames?.nodes || []}
      futureGames={futureGames}
      onClick={id => openDialog(DialogKey.game, id)}
      infiniteScrollLoader={<InfiniteScrollLoader />}
      tab={tab}
      setTab={setTab}
    />
  );
};

export default Fixtures;
